import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useId, useRef, useState } from 'react';

import { CanvassingSessionExportStatus } from '../../graphql/__generated__/graphql';
import type { CanvassingSessionFetchImpl } from './canvassingSessionApi';
import { buildCanvassingSessionFilterVariables } from './canvassingSessionApi';
import type { CanvassingSessionFilterState } from './canvassingSessionFilters';
import { describeCanvassingSessionFilters } from './canvassingSessionFilterSummary';
import { CanvassingSessionExportDownloadButton } from './CanvassingSessionExportDownloadButton';
import { useCanvassingSessionExport } from './useCanvassingSessionExport';

const CONFIRMATION_BODY =
  'Export all sessions matching these filters and their door visits. This includes all matching pages, not only the visible rows. Transcripts are fetched from Core; missing or failed reads stay blank. A server restart interrupts the export, and it does not resume.';

type ConfirmationSnapshot = {
  filter: ReturnType<typeof buildCanvassingSessionFilterVariables>;
  listTotal: number;
  summary: string;
};

export type CanvassingSessionExportProps = {
  filters: CanvassingSessionFilterState;
  listTotal: number;
  disabled: boolean;
  fetchImpl?: CanvassingSessionFetchImpl;
  resolveCanvasserLabel: (canvasserId: string) => string;
};

export const CanvassingSessionExport = ({
  filters,
  listTotal,
  disabled,
  fetchImpl,
  resolveCanvasserLabel,
}: CanvassingSessionExportProps) => {
  const id = useId();
  const exportAction = useCanvassingSessionExport({ fetchImpl });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snapshot, setSnapshot] = useState<ConfirmationSnapshot | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const actionRegionRef = useRef<HTMLDivElement | null>(null);
  const restoreFocusRef = useRef(false);
  const dialogOpenRef = useRef(false);
  const submissionLockRef = useRef(false);
  const pending = exportAction.state.startPending;
  const exportRunning = exportAction.state.latest?.status === CanvassingSessionExportStatus.Running;
  const exportDisabled = disabled || pending || exportAction.state.latestLoading || exportRunning;
  const titleId = `${id}-title`;
  const descriptionId = `${id}-description`;
  const summaryId = `${id}-summary`;

  const openConfirmation = (): void => {
    if (exportDisabled) return;
    setSnapshot({
      filter: buildCanvassingSessionFilterVariables(filters),
      listTotal,
      summary: describeCanvassingSessionFilters(filters, resolveCanvasserLabel),
    });
    dialogOpenRef.current = true;
    setDialogOpen(true);
  };

  const latest = exportAction.state.latest;

  const closeConfirmation = (): void => {
    restoreFocusRef.current = true;
    dialogOpenRef.current = false;
    setDialogOpen(false);
  };

  const confirm = async (): Promise<void> => {
    if (snapshot === null || submissionLockRef.current || pending) return;
    submissionLockRef.current = true;
    try {
      await exportAction.start(snapshot.filter);
    } finally {
      submissionLockRef.current = false;
      if (dialogOpenRef.current) closeConfirmation();
    }
  };

  return (
    <>
      <Box
        ref={actionRegionRef}
        role="group"
        aria-label="Session export action"
        tabIndex={-1}
        sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 0 }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ alignItems: { sm: 'center' }, flexWrap: 'wrap', minWidth: 0 }}>
          <Button variant="outlined" onClick={openConfirmation} disabled={exportDisabled} aria-busy={pending} ref={triggerRef}>
            {pending ? 'Starting export…' : 'Export Sessions'}
          </Button>
          {exportAction.state.latestLoading && (
            <Typography role="status" aria-live="polite" variant="body2">Checking latest export…</Typography>
          )}
          {exportAction.state.latestError !== null && (
            <Alert
              severity="warning"
              action={<Button color="inherit" size="small" onClick={exportAction.refreshLatest} disabled={exportAction.state.latestLoading}>Retry</Button>}
            >
              {exportAction.state.latestError}
            </Alert>
          )}
          {latest?.status === CanvassingSessionExportStatus.NoMatches && (
            <Typography role="status" aria-live="polite" variant="body2">No sessions match these filters.</Typography>
          )}
          {latest?.status === CanvassingSessionExportStatus.Running && (
            <Typography role="status" aria-live="polite" variant="body2">Generating export…</Typography>
          )}
          {latest?.status === CanvassingSessionExportStatus.Completed && (
            <CanvassingSessionExportDownloadButton exportItem={latest} fetchImpl={fetchImpl} />
          )}
          {latest?.status === CanvassingSessionExportStatus.Failed && (
            <Alert severity="error" role="alert" aria-live="assertive">
              {latest.failureSummary || 'The session export could not be completed. Try again.'}
            </Alert>
          )}
          {exportAction.state.pollingError !== null && (
            <Alert severity="warning" role="alert">{exportAction.state.pollingError}</Alert>
          )}
          {exportAction.state.startError !== null && (
            <Alert severity={exportAction.state.startError.uncertain ? 'warning' : 'error'} role="alert" aria-live="assertive">
              {exportAction.state.startError.message}
            </Alert>
          )}
        </Stack>
      </Box>
      <Dialog
        open={dialogOpen}
        onClose={closeConfirmation}
        aria-labelledby={titleId}
        aria-describedby={`${descriptionId} ${summaryId}`}
        aria-busy={pending}
        slotProps={{
          paper: { 'aria-busy': pending },
          transition: {
            onExited: () => {
              if (!restoreFocusRef.current) return;
              restoreFocusRef.current = false;
              if (triggerRef.current?.disabled) actionRegionRef.current?.focus();
              else triggerRef.current?.focus();
            },
          },
        }}
      >
        <DialogTitle id={titleId}>Export sessions?</DialogTitle>
        <DialogContent>
          <DialogContentText id={descriptionId}>{CONFIRMATION_BODY}</DialogContentText>
          {snapshot !== null && (
            <DialogContentText id={summaryId} sx={{ mt: 1, overflowWrap: 'anywhere' }}>
              Matching list total: {snapshot.listTotal}. {snapshot.summary} This total is informational only.
            </DialogContentText>
          )}
          {pending && (
            <Stack role="status" aria-live="polite" direction="row" spacing={1} sx={{ alignItems: 'center', mt: 2 }}>
              <CircularProgress size={20} aria-hidden />
              <Typography variant="body2">Starting export…</Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={closeConfirmation}>Cancel</Button>
          <Button variant="contained" onClick={() => void confirm()} disabled={pending || snapshot === null}>
            {pending ? 'Starting…' : 'Export'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
